import { cn } from "@fronterahealth/frontera-ui-components/lib/utils";

// One absolutely positioned slot in the ReviewPanel stack, shared by thread cards and the draft
// composer. Positioning (top/registerRef) is owned entirely by the caller via useAnchoredStack.

// Content is click-through to the go-to button except these; give any other click target pointer-events-auto.
// The input group is the edit and reply box, whose padding must not take focus from its textarea.
const CLICKABLE_CONTENT =
  "[&_:is(a,button,input,label,select,textarea,[role=button],[tabindex],[title],[data-slot=input-group])]:pointer-events-auto";

/** The active card's edge, in rio to pair with its highlighted field. */
const ACTIVE_CARD_EDGE = "[&_[data-slot=card]]:inset-shadow-[3px_0_0_var(--color-rio-600)]";

interface ActivatableCardProps {
  label: string;
  onClick: () => void;
  active?: boolean;
  children: React.ReactNode;
}

/**
 * A card whose clicks, outside its own controls, go to a button stretched under it. The button fills the nearest
 * positioned ancestor, so the caller supplies one.
 */
export const ActivatableCard: React.FC<ActivatableCardProps> = ({ label, onClick, active = false, children }) => (
  <>
    <button
      type="button"
      aria-label={label}
      data-dd-action-name="go to field"
      onClick={onClick}
      className="peer absolute inset-0 outline-hidden select-none"
    />
    <div
      className={cn(
        "pointer-events-none relative rounded-lg peer-focus-visible:ring-2 peer-focus-visible:ring-dynamic-stroke-primary-default peer-focus-visible:ring-offset-2",
        CLICKABLE_CONTENT,
        // On the card itself, so the edge moves with the active card's shift and sits under its shadow.
        active && ACTIVE_CARD_EDGE,
      )}
    >
      {children}
    </div>
  </>
);

interface StackSlotProps {
  top: number;
  registerRef: (el: HTMLDivElement | null) => void;
  /** Kept mounted (for height measurement) but not painted, e.g. scrolled out of the right panel. */
  hidden?: boolean;
  /**
   * Hide with `visibility` instead of `display`, so a slot that was never painted still measures its
   * real height. For short slots (resolved summaries), whose height is far below the card estimate.
   */
  measureWhileHidden?: boolean;
  /** A button stretched under the slot's content, which takes every click the content passes through. */
  activate?: { label: string; onClick: () => void };
  /** The active card, edged in rio to pair with its highlighted field. */
  active?: boolean;
  children: React.ReactNode;
}

export const StackSlot: React.FC<StackSlotProps> = ({
  top,
  registerRef,
  hidden = false,
  measureWhileHidden = false,
  activate,
  active = false,
  children,
}) => (
  <div
    ref={registerRef}
    className="absolute left-0 w-full px-3"
    style={{
      top: `${top}px`,
      display: hidden && !measureWhileHidden ? "none" : "block",
      visibility: hidden && measureWhileHidden ? "hidden" : undefined,
      transition: "top 200ms cubic-bezier(.2,.9,.2,1)",
      willChange: "top",
    }}
  >
    {activate ? (
      <ActivatableCard label={activate.label} onClick={activate.onClick} active={active}>
        {children}
      </ActivatableCard>
    ) : (
      children
    )}
  </div>
);
